/* 
 * This file is created by Vatsaly Patel
 * Note: If you are editing anything, please let me know - or do comment [it's compulsory]
*/

import React, { useCallback, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import { useHistory } from "react-router-dom";
import appRoutes from "../../../routes/appRoutes";
import { CircularProgress } from "@material-ui/core";
import { acceptAssessmentAPI, getOrderHistoryWithFilterAPI } from "../../../apiRoutes";
import moment from "moment-timezone";
import Label from "../../../components/label";
import { MAPPED_ORDER_HISTORY_STATUS, ORDER_HISTORY_FILTERS } from "../../../constants/helperConstants";
import ICButton from "../../../components/button";
import { setOnlineAssessmentStatus } from "../../../reduxStore/actions/commonAction";
import { removeAssessmentData } from "../../../reduxStore/actions/auth";
import Model from "../../../components/modal";

function createData(
  _id,
  appointmentId,
  patientName,
  submittedDate,
  status,
  doctorName,
  doctorUIN,
  patientNameOnly,
  patientUIN,
  completedDate,
  assessmentType,
  patinetPreferredTime
) {
  return {
    _id,
    appointmentId,
    patientName,
    submittedDate,
    status,
    doctorName,
    doctorUIN,
    patientNameOnly,
    patientUIN,
    completedDate,
    assessmentType,
    patinetPreferredTime
  };
}

const AssessmentListTable = (props) => {
  const history = useHistory(); // go to other page with or without data
  const observer = useRef(); // to check it is the last element or not - if it is last element then call an API
  const [pageNo, setPageNumber] = useState(1); // just set page number if needed in future.
  const [nextPage, setNextPage] = useState(false); // set next page if there is more data
  const [loading, setLoading] = useState(true); // loading variable used to reduce multiple API calls
  const [progressBar, setProgressBar] = useState(false); // progress bar hide and show
  const [limit, setLimit] = useState(10); // limit is fixed but if we need to change in future then change it here
  const [skip, setSkip] = useState(0); // set Skip page
  const [noMoreData, setNoMoreData] = useState(false); // no data found block hide-show
  const [showNoPrescriberNumberModal, setShowNoPrescriberNumberModal] = useState(false);
  // Column Names - if you want to add an column then add id and label here - also take care of createData function (need to pass data)
  const columns = [
    {
      id: "submittedDate",
      label: "Date Received"
    },
    {
      id: "patientName",
      label: "Patient"
    },
    {
      id: "assessmentType",
      label: "Type"
    },
    {
      id: "status",
      label: "Status"
    },
    {
      id: "patinetPreferredTime",
      label: "Contact"
    },
    {
      id: "action",
      label: "Action"
    }
  ];

  // Data to be appended
  const [rows, setRows] = useState([]); // setting rows

  /*
 * Go to accept assessment page - need to pass _id to get the data on page
 */
  const goToAcceptAssessmentPage = async (event, row) => {
    if (row.assessmentType === "Medicinal Cannabis" && !props.auth?.userData?.authPrescriberSchemaNoMC) {
      setShowNoPrescriberNumberModal(true);
      return;
    }

    if (row.assessmentType === "Smoking Cessation" && !props.auth?.userData?.authPrescriberSchemaNo) {
      setShowNoPrescriberNumberModal(true);
      return;
    }

    const no = row._id;

    await props.removeAssessmentData();
    const dataToSend = {
      assessmentId: no
    };
    let apiCallParam = {
      reqType: "POST",
      reqURL: acceptAssessmentAPI,
      dataObj: dataToSend,
      params: {},
      headers: {},
      successCallback: async res => {
        console.log("Got the assessment details: ", res);
        await props.setOnlineAssessmentStatus(true);
        history.push(appRoutes.instantSwitchAcceptAssessment + "/" + no, { _id: no });
      },
      failureCallback: err => {
        console.log("Something went wrong. Please try again ", err);
        resetPagination();
        setRows([]);
        getOrders(limit, 0, props.statusFilter, 1);
      }
    };
    props.makeAPICall(apiCallParam);
  };

  const viewAssessmentPage = async (event, no) => {
    await props.removeAssessmentData();
    await props.setOnlineAssessmentStatus(true);
    history.push(appRoutes.instantSwitchAcceptAssessment + "/" + no, { _id: no });
  };

  const onlyViewAssessment = (event, no) => {
    history.push(appRoutes.instantSwitchViewCompletedAssessment + "/" + no, { _id: no });
  };

  /*
   * Check last element is visible or not on scroll
  */
  const lastElementRef = useCallback(node => {
    if (observer.current) observer.current.disconnect();
    observer.current = new IntersectionObserver(data => {
      if (data) {
        if (data[0].isIntersecting && nextPage && loading) {
          setLoading(false);
          if (loading) {
            let skipCalculate = limit * pageNo,
              currentPageNo = pageNo + 1;
            setPageNumber(currentPageNo);
            setSkip(skipCalculate);
            getOrders(limit, skipCalculate, props.statusFilter, currentPageNo);
          }
          setTimeout(() => {
            setLoading(true);
          }, 2000);
        }
      }
    });
    if (node) observer.current.observe(node);
  });

  const getOrders = useCallback((limitPage, skipPage, filterStatus, currentPage) => {
    let updatedParam = {
      limit: limitPage,
      skip: skipPage,
      assessmentType: props.assessmentType
    };

    if (filterStatus !== "") updatedParam["assessmentStatus"] = filterStatus;

    if (filterStatus !== ORDER_HISTORY_FILTERS.TO_BE_ASSESSED) {
      updatedParam["doctorId"] = props.doctorId;
    }

    let apiCallParam = {
      reqType: "GET",
      reqURL: getOrderHistoryWithFilterAPI,
      dataObj: {},
      params: updatedParam,
      headers: {},
      successCallback: res => {
        // calculate is there any next page or not ?
        let currentRowCount = res.data.data.orderHistoryDataCount - (currentPage * limitPage);
        if (currentRowCount > 0) {
          setNextPage(true);
        } else {
          setNextPage(false);
          setNoMoreData(true);
          setProgressBar(false);
        }

        // set the data
        let orderData = [];

        res.data.data.orderHistoryData.map((assessment, index) => {
          // let status = 'Draft';
          // if (assessment.assessmentStatus === ORDER_HISTORY_FILTERS.COMPLETED) {
          //     status = 'Completed';
          // } else if (assessment.assessmentStatus === ORDER_HISTORY_FILTERS.TO_BE_ASSESSED) {
          //     status = 'To Be Assessed';
          // } else if (assessment.assessmentStatus === ORDER_HISTORY_FILTERS.ACCEPTED) {
          //     status = 'Accepted';
          // } else {
          //     status = assessment.assessmentStatus;
          // }

          let patinetPreferredTime = "-";
          if (assessment.doctorContactByTime && assessment.patinetPreferredTime) {
            patinetPreferredTime = assessment.patinetPreferredTime;
          } else if (assessment.doctorContactByTime) {
            patinetPreferredTime = assessment.doctorContactByTime;
          }

          orderData.push(
            // createData used for Material UI table to set the data (if you include anything, don't forget to include in createData function at the top or bottom of the file)
            createData(
              assessment._id,
              assessment.appointmentId,
              assessment.patientId ? ("UIN: " + assessment.patientId.patientId + " | " + assessment.patientId.firstName + " " + assessment.patientId.lastName) : "-",
              (assessment.submittedDate ? moment(assessment.submittedDate).tz("Australia/Brisbane").format("DD-MM-YYYY") : "-"),
              assessment.assessmentStatus,
              assessment.doctorId ? (assessment.doctorId.firstName + " " + assessment.doctorId.lastName) : "-",
              assessment.doctorId ? assessment.doctorId.doctorId : "-",
              assessment.patientId ? assessment.patientId.firstName + " " + assessment.patientId.lastName : "-",
              assessment.patientId ? assessment.patientId.patientId : "-",
              assessment.completedDate ? moment(assessment.completedDate).tz("Australia/Brisbane").format("DD-MM-YYYY") : "-",
              assessment.assessmentType || "Smoking Cessation",
              patinetPreferredTime
            )
          );
        });

        if (skipPage === 0) {
          setRows(orderData);
        } else {
          setRows(prev => prev.concat(orderData));
        }
      },
      failureCallback: err => {
        console.log("Something went wrong. Please try again ", err);
        setNextPage(false);
        setNoMoreData(true);
        setProgressBar(false);
      },
      showPopUp: false
    };
    props.makeAPICall(apiCallParam);
  });

  /*
   * Watch on filter
  */
  useEffect(() => {
    resetPagination();
    setRows([]);
    getOrders(limit, 0, props.statusFilter, 1);
  }, [props.statusFilter]);

  /*
  * Watch on rows
  */
  useEffect(() => {
    props.updateAssessmentData(rows);
  }, [rows]);

  /*
  * Reset pagination
  */
  const resetPagination = () => {
    setPageNumber(1);
    setNextPage(false);
    setLoading(true);
    setProgressBar(true);
    setSkip(0);
    setNoMoreData(false);
  };

  return (
    <Paper className="w-100 assessment-order-list-tbl-div">
      <TableContainer className="table-max-height primary-table-color">
        <Table stickyHeader aria-label="sticky table">
          <TableHead>
            {/* Loop on Column name  */}
            <TableRow>
              {columns.map(column => (
                <TableCell
                  key={column.id}
                  align={column.align}
                  style={{ minWidth: column.minWidth }}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {/* Loop over the data (createData function) */}
            {rows.map((row, index) => {
              /*
              * If it is last element then take the referent to call an API Else print another block
              */
              if (rows.length === index + 1) {
                return (
                  <TableRow
                    ref={lastElementRef}
                    hover
                    key={row._id}
                  >
                    <TableCell>
                      {row.submittedDate}
                    </TableCell>
                    <TableCell>{row.patientName} </TableCell>
                    <TableCell>{row.assessmentType} </TableCell>
                    <TableCell>{MAPPED_ORDER_HISTORY_STATUS[row.status]} </TableCell>
                    <TableCell>{row.patinetPreferredTime} AEST</TableCell>
                    <TableCell>
                      {
                        row.status === ORDER_HISTORY_FILTERS.TO_BE_ASSESSED && (
                          <ICButton
                            title="Assess & Treat"
                            className="gray small gray-outline"
                            clickEvent={e => goToAcceptAssessmentPage(e, row)}
                          />
                        )
                      }

                      {
                        row.status === ORDER_HISTORY_FILTERS.ACCEPTED && (
                          <ICButton
                            title="Accepted"
                            className="gray small gray-outline"
                            clickEvent={e => viewAssessmentPage(e, row._id)}
                          />
                        )
                      }

                      {
                        (row.status !== ORDER_HISTORY_FILTERS.TO_BE_ASSESSED && row.status !== ORDER_HISTORY_FILTERS.ACCEPTED) && (
                          <ICButton
                            title="View"
                            className="gray small gray-outline"
                            clickEvent={e => onlyViewAssessment(e, row._id)}
                          />
                        )
                      }

                    </TableCell>
                  </TableRow>
                );
              } else {
                return (
                  <TableRow
                    hover
                    key={row._id}
                  >
                    <TableCell> {row.submittedDate} </TableCell>
                    <TableCell>{row.patientName} </TableCell>
                    <TableCell>{row.assessmentType} </TableCell>
                    <TableCell>{MAPPED_ORDER_HISTORY_STATUS[row.status]} </TableCell>
                    <TableCell>{row.patinetPreferredTime} AEST</TableCell>
                    <TableCell>
                      {
                        row.status === ORDER_HISTORY_FILTERS.TO_BE_ASSESSED && (
                          <ICButton
                            title="Assess & Treat"
                            className="gray small gray-outline"
                            clickEvent={e => goToAcceptAssessmentPage(e, row)}
                          />
                        )
                      }

                      {
                        row.status === ORDER_HISTORY_FILTERS.ACCEPTED && (
                          <ICButton
                            title="Accepted"
                            className="gray small gray-outline"
                            clickEvent={e => viewAssessmentPage(e, row._id)}
                          />
                        )
                      }

                      {
                        (row.status !== ORDER_HISTORY_FILTERS.TO_BE_ASSESSED && row.status !== ORDER_HISTORY_FILTERS.ACCEPTED) && (
                          <ICButton
                            title="View"
                            className="gray small gray-outline"
                            clickEvent={e => onlyViewAssessment(e, row._id)}
                          />
                        )
                      }
                    </TableCell>
                  </TableRow>
                );
              }
            })}

            {/*
                  * Progress bar on loading APIs 
            */}

            <TableRow className={progressBar ? "" : "d-none"}>
              <TableCell colSpan={5} className="text-center">
                <CircularProgress disableShrink className="primary-loader" />
              </TableCell>
            </TableRow>

            {/*
                * No data fount if there are no data array in API or clear btn clicked
            */}

            <TableRow
              className={(noMoreData && !rows.length) || (noMoreData && rows.length === limit) || (noMoreData && pageNo > 1) ? "" : "d-none"}>
              <TableCell
                colSpan={6}
                className="text-center progress-bar-center border-bottom-0"
              >
                <Label title="No data found" />
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      <Model
        openModal={showNoPrescriberNumberModal}
        modalBodyContent={"You do not have an Authorised Prescriber Scheme Prescriber Number <br>registered with Instant Consult for this type of online assessment<br><br>Please select another type of treatment to be assessed"}
        okayText="Okay"
        cancelText="Okay"
        title="Instant Consult"
        useOneButton={true}
        className="modal-lg"
        okayEvent={() => setShowNoPrescriberNumberModal(false)}
        cancelEvent={() => setShowNoPrescriberNumberModal(false)}
      />
    </Paper>
  );
};

const mapStateToProps = props => ({
  auth: props.auth.user,
  commonReducer: props.commonReducers
});

const mapDispatchToProps = dispatch => ({
  setOnlineAssessmentStatus: data => dispatch(setOnlineAssessmentStatus(data)),
  removeAssessmentData: () => dispatch(removeAssessmentData())
});

export default connect(mapStateToProps, mapDispatchToProps)(AssessmentListTable);
